// Phase 15 M2 — Animal detail / sale detail page.
//
// Wraps `GET /api/v1/marketplace/sales/:id` (PRD §M2 — the sale envelope is
// the authoritative read; it carries the animal block + the commercial
// status). Anonymous reads work because the backend lets unauthenticated
// callers fetch LISTED sales.
//
// Lote V-3 (F-034) — A páxina simplificouse drasticamente: eliminamos as
// tabs (Resumo / Timeline / Xenealoxía / Sanidade / Documentos) porque o
// QR público do animal xa leva ao historial completo (mesmo formato que
// ve o consumidor na carnicería). A páxina é agora hero + header + aside
// vendedor + QR. Único formato. Os datos completos do animal (eventos,
// xenealoxía, sanidade, docs) vense en `chain.leyro.es/qr/<qr_code>`.

import axios from 'axios';
import { Share2 } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { MarketplaceSaleDetail, MarketplaceSaleStatus } from '@leyrochain/shared';

import InfoCallout from '../components/InfoCallout';
import SanitaryIndicator, { type SanitaryLevel } from '../components/SanitaryIndicator';
import { resendVerification } from '../api/auth';
import { openConversation } from '../api/conversations';
import { describeAxiosError } from '../api/errors';
import { getSale } from '../api/marketplace';
import { useAuth } from '../auth/useAuth';
import { useToast } from '../context/ToastContext';
import { describeGateReason, useGate, type GateReason } from '../hooks/useGate';

const STATUS_LABEL: Record<MarketplaceSaleStatus, string> = {
  LISTED: 'En venda',
  OFFER_PLACED: 'Oferta recibida',
  AUCTION_WON: 'Subasta ganada — pendente de pagar',
  RESERVED: 'Reservado',
  PAYMENT_HELD: 'Pagamento en escrow',
  CONTRACT_PENDING: 'Contrato pendente',
  CONFIRMED: 'Confirmada',
  IN_TRANSIT: 'En transporte',
  DELIVERED: 'Entregado',
  COMPLETED: 'Finalizada',
  CANCELLED: 'Cancelada',
  DISPUTED: 'En disputa',
  RESOLVED: 'Resolta',
};

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

function sanitaryLevelFor(sale: MarketplaceSaleDetail): SanitaryLevel {
  if (sale.dispute_opened_at) return 'RED';
  if (sale.sanitary_declaration_at) return 'GREEN';
  return 'UNKNOWN';
}

// Lote V-3 (F-034) — base do QR público; mesma URL que ve o consumidor na
// carnicería ao escanear o ulster do animal. É o único formato — non hai
// outra "ficha" descritiva no marketplace.
const PUBLIC_QR_BASE = 'https://chain.leyro.es/qr';

export default function AnimalDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const gate = useGate();
  const [contactPending, setContactPending] = useState(false);
  const [sale, setSale] = useState<MarketplaceSaleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getSale(id)
      .then((data) => {
        if (!alive) return;
        setSale(data);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('Este animal non está dispoñible no marketplace.');
        } else {
          setError('Non se puido cargar a información. Recarga a páxina nun momento.');
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="h-72 rounded-xl bg-border/40 animate-pulse" />
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center">
        <p className="text-lg font-semibold text-dark">{error ?? 'Animal non atopado.'}</p>
        <Link to="/marketplace" className="inline-block mt-4 text-rauxa underline">
          Volver ao marketplace
        </Link>
      </div>
    );
  }

  const canReserve = sale.status === 'LISTED' && (!user || user.id !== sale.buyer_id);
  const isMyPurchase = sale.buyer_id && user && user.id === sale.buyer_id;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Breadcrumb + action row — port de Leyro ListingDetailPage.jsx */}
      <DetailHeader title={sale.animal.name ?? sale.animal.crotal_id} />

      {/* Hero — imaxe full-width Airbnb-style */}
      <div className="rounded-3xl overflow-hidden mb-8 relative">
        {sale.animal.photo_url ? (
          <img
            src={sale.animal.photo_url}
            alt={sale.animal.name ?? sale.animal.crotal_id}
            className="w-full h-[420px] object-cover"
          />
        ) : (
          <div className="w-full h-[420px] bg-subtle flex items-center justify-center text-rauxa/30">
            <span className="text-9xl">🐄</span>
          </div>
        )}
      </div>

      {/* Title + price strip */}
      <div className="grid md:grid-cols-[2fr_1fr] gap-8">
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <h1 className="text-3xl font-extrabold text-dark">
              {sale.animal.name ?? sale.animal.crotal_id}
            </h1>
          </div>
          <p className="text-sm text-muted">
            {[sale.animal.crotal_id, sale.animal.breed, sale.animal.sex]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <span className="text-[11px] font-bold text-chain bg-chain-soft px-2.5 py-1 rounded-full uppercase tracking-wide">
              {STATUS_LABEL[sale.status]}
            </span>
            <SanitaryIndicator level={sanitaryLevelFor(sale)} />
          </div>
        </div>

        {/* Sticky sale card á dereita */}
        <aside className="bg-white rounded-2xl border border-border p-6 flex flex-col gap-4 md:sticky md:top-24 self-start shadow-card">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-rauxa">
              {formatPrice(sale.price_cents)}
            </span>
            <span className="text-xs text-muted">
              {sale.sale_mode === 'AUCTION' ? 'Subasta' : 'Prezo fixado'}
            </span>
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-xs uppercase tracking-wide text-muted mb-1">Vendedor</p>
            <p className="font-semibold text-dark">{sale.seller.farm_name ?? sale.seller.name}</p>
            {sale.seller.farm_municipality ? (
              <p className="text-sm text-muted">{sale.seller.farm_municipality}</p>
            ) : null}
            {sale.seller.verified ? (
              <span className="inline-block mt-2 text-[10px] font-bold text-rauxa bg-rauxa/10 px-2 py-0.5 rounded-full">
                ✓ Granxa verificada
              </span>
            ) : null}
          </div>

          {canReserve ? (
            gate.canReserve.allowed ? (
              <button
                type="button"
                onClick={() => navigate(`/marketplace/checkout/${sale.id}`)}
                className="btn-primary w-full"
                aria-label="reservar"
              >
                Reservar
              </button>
            ) : (
              <ReserveBlocked
                reasons={gate.canReserve.reasons}
                loggedIn={gate.isLoggedIn}
                userEmail={user?.email ?? null}
              />
            )
          ) : sale.status === 'RESERVED' ? (
            <p className="text-sm text-muted text-center">Reservado por outro comprador.</p>
          ) : null}

          {isMyPurchase ? (
            <Link to="/marketplace/me/purchases" className="btn-outline w-full text-center">
              Ver miñas compras
            </Link>
          ) : null}

          {/* Phase 25 iter-8 — chat co vendedor (oculto se son eu mesmo). */}
          {user && sale.seller.id !== user.id ? (
            <button
              type="button"
              disabled={contactPending}
              onClick={async () => {
                setContactPending(true);
                try {
                  const { id: convId } = await openConversation(sale.id);
                  navigate(`/marketplace/messages/${convId}`);
                } catch (err) {
                  addToast(describeAxiosError(err, 'Non se puido abrir a conversa.'), 'error');
                } finally {
                  setContactPending(false);
                }
              }}
              className="btn-outline w-full text-center disabled:opacity-60"
            >
              {contactPending ? 'Abrindo…' : 'Contactar vendedor'}
            </button>
          ) : null}
        </aside>
      </div>

      {/* Lote V-3 (F-034) — Único panel de información: QR público + enlace. */}
      <div className="mt-6 mb-4">
        <InfoCallout
          storageKey="animal-public-qr"
          defaultOpen={true}
          icon={<span aria-hidden>📱</span>}
          title="Que é o QR público?"
          body="Este QR leva ao historial completo do animal: idade, raza, nacemento, traslados, vacinas, propietarios anteriores. É o mesmo que verá o consumidor final na carnicería ao escanealo. Compártelo cos compradores para que comproben antes de mercar."
        />
      </div>
      <QrPanel qrCode={sale.animal.qr_code} crotalId={sale.animal.crotal_id} />
    </div>
  );
}

function QrPanel({ qrCode, crotalId }: { qrCode: string; crotalId: string }): JSX.Element {
  const publicUrl = `${PUBLIC_QR_BASE}/${qrCode}`;
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(publicUrl, { width: 320, margin: 1 })
      .then((url) => {
        if (alive) setQrDataUrl(url);
      })
      .catch(() => {
        if (alive) setQrDataUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [publicUrl]);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard bloqueado (ex. http inseguro) — o usuario sempre pode
       * abrir o enlace e copialo da barra do navegador. */
    }
  };

  return (
    <section className="mt-10 flex flex-col items-center gap-4 py-6 border-t border-border">
      <h2 className="text-xl font-bold text-dark">Historial público do animal</h2>
      <p className="text-sm text-muted text-center max-w-md">
        Este é o QR público do animal. É o mesmo que verá o consumidor final na carnicería e leva ao
        seu historial completo (nacemento, granxas, traslados, sanidade). Escaneao co teu móbil ou
        abre o enlace para comprobar toda a información antes de mercar.
      </p>

      <div
        className="rounded-2xl bg-white border border-border p-4 shadow-card"
        aria-label={`QR público do animal ${crotalId}`}
      >
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="QR público" className="w-64 h-64" />
        ) : (
          <div className="w-64 h-64 bg-border/40 animate-pulse rounded" />
        )}
      </div>

      <div className="flex flex-col items-center gap-2 w-full max-w-md">
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-mono text-rauxa underline break-all text-center"
        >
          {publicUrl}
        </a>
        <button
          type="button"
          onClick={onCopy}
          className="btn-outline text-sm"
          aria-label="copiar enlace"
        >
          {copied ? '✓ Copiado' : 'Copiar enlace'}
        </button>
      </div>
    </section>
  );
}

function ReserveBlocked({
  reasons,
  loggedIn,
  userEmail,
}: {
  reasons: GateReason[];
  loggedIn: boolean;
  userEmail: string | null;
}): JSX.Element {
  const { addToast } = useToast();
  const [resending, setResending] = useState(false);

  if (!loggedIn) {
    return (
      <Link to="/marketplace/login?next=/marketplace" className="btn-primary w-full text-center">
        Inicia sesión para reservar
      </Link>
    );
  }
  if (reasons.includes('NEED_KYC')) {
    return (
      <>
        <Link to="/marketplace/kyc" className="btn-primary w-full text-center">
          Verificar identidade para reservar
        </Link>
        <p className="text-xs text-muted text-center mt-1">
          Antes de mercar precisamos confirmar a túa identidade.
        </p>
      </>
    );
  }
  if (reasons.includes('NEED_ROLE_BUYER')) {
    return (
      <>
        <Link to="/marketplace/profile/roles" className="btn-outline w-full text-center">
          Engadir rol Comprador/a
        </Link>
        <p className="text-xs text-muted text-center mt-1">
          A túa conta non ten o rol de comprador activo.
        </p>
      </>
    );
  }
  if (reasons.includes('NEED_EMAIL_VERIFIED')) {
    // Lote V-3 followup (F-021) — antes só mostraba a mensaxe sen acción.
    // Agora ofrecemos un botón directo de re-envío do correo de
    // verificación (require que o usuario teña email — sempre o ten
    // logueado).
    const onResend = async (): Promise<void> => {
      if (!userEmail) {
        addToast('Falta o teu correo na conta. Engádeo dende o perfil.', 'error');
        return;
      }
      setResending(true);
      try {
        await resendVerification({ email: userEmail });
        addToast('Enviamos un novo correo de verificación. Revisa a túa bandeja.', 'success');
      } catch (err) {
        addToast(describeAxiosError(err, 'Non se puido enviar o correo.'), 'error');
      } finally {
        setResending(false);
      }
    };
    return (
      <>
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="btn-primary w-full disabled:opacity-60"
        >
          {resending ? 'Enviando…' : 'Enviar correo de verificación'}
        </button>
        <p className="text-xs text-muted text-center mt-1">
          Verifica o teu email antes de poder reservar.
        </p>
      </>
    );
  }
  return (
    <p className="text-sm text-muted text-center">{reasons.map(describeGateReason).join(' · ')}</p>
  );
}

// Lote UX Leyro parity — breadcrumb + action row (Share) sobre o hero.
// Port simplificado de Leyro `ListingDetailPage.jsx` liñas 188-265.
function DetailHeader({ title }: { title: string }): JSX.Element {
  const { addToast } = useToast();
  return (
    <div className="mb-4">
      <nav
        className="text-sm text-muted flex items-center gap-1.5 flex-wrap mb-3"
        aria-label="breadcrumb"
      >
        <Link to="/marketplace" className="hover:underline hover:text-dark transition-colors">
          Marketplace
        </Link>
        <span>/</span>
        <span className="text-dark truncate max-w-[260px]">{title}</span>
      </nav>
      <div className="flex items-center justify-end">
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-subtle text-sm font-medium text-dark transition-colors"
          onClick={async () => {
            const shareData: ShareData = {
              title,
              text: `${title} en LeyroChain`,
              url: window.location.href,
            };
            try {
              if (navigator.share && (navigator.canShare?.(shareData) ?? true)) {
                await navigator.share(shareData);
                return;
              }
            } catch (err) {
              if ((err as { name?: string })?.name === 'AbortError') return;
            }
            try {
              await navigator.clipboard?.writeText(window.location.href);
              addToast('Ligazón copiada', 'success');
            } catch {
              /* clipboard bloqueado (http inseguro, permisos) — silencioso */
            }
          }}
          aria-label="compartir"
        >
          <Share2 size={16} />
          Compartir
        </button>
      </div>
    </div>
  );
}
